/**
 * Assignment Service
 * ==================
 * Handles program assignments to clients and progress tracking.
 */

import { supabase } from '../lib/supabase'
import { emailService } from './emailService'
import type {
  Assignment,
  AssignmentInsert,
  AssignmentUpdate,
  AssignmentProgress,
  AssignmentStatus,
  Client,
  Program,
  ProgramItem,
  Video,
} from '../types/database.types'

// Email cooldown period in minutes
const EMAIL_COOLDOWN_MINUTES = 5

export interface AssignmentWithDetails extends Assignment {
  client: Pick<Client, 'id' | 'full_name' | 'email'>
  program: Pick<Program, 'id' | 'title' | 'description' | 'duration_weeks'>
  progress_summary?: {
    total_items: number
    completed_items: number
    progress_percent: number
  }
}

export interface AssignmentFilters {
  status?: AssignmentStatus
  clientId?: string
  programId?: string
}

/**
 * Summary of assignments for a program (used for ProgramCard preview)
 */
export interface AssignmentSummary {
  program_id: string
  active_count: number
  completed_count: number
  expired_count: number
  total_count: number
  average_progress: number
  last_activity?: string
}

export interface ProgramItemWithProgress extends ProgramItem {
  video: Pick<Video, 'id' | 'title' | 'thumbnail_url' | 'duration_seconds' | 'mux_playback_id' | 'status'>
  progress?: AssignmentProgress | null
}

export interface AssignmentWithProgress extends Assignment {
  program: Program & {
    items: ProgramItemWithProgress[]
  }
  client: Client
}

/**
 * Result of sending an assignment email
 */
export interface AssignmentEmailResult {
  assignmentId: string
  success: boolean
  error?: string
}

/**
 * Assignment with client email for email sending
 */
export interface AssignmentWithClientEmail extends Assignment {
  client: Pick<Client, 'id' | 'full_name' | 'email'>
}

export const assignmentService = {
  /**
   * Get all assignments for a provider
   */
  async getAssignments(
    providerId: string,
    filters?: AssignmentFilters
  ): Promise<AssignmentWithDetails[]> {
    let query = supabase
      .from('assignments')
      .select(
        `
        *,
        client:clients (id, full_name, email),
        program:programs (id, title, description, duration_weeks)
      `
      )
      .eq('provider_id' as any, providerId)
      .order('created_at', { ascending: false }) as any

    if (filters?.status) {
      query = query.eq('status' as any, filters.status)
    }

    if (filters?.clientId) {
      query = query.eq('client_id' as any, filters.clientId)
    }

    if (filters?.programId) {
      query = query.eq('program_id' as any, filters.programId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch assignments:', error)
      throw error
    }

    return (data || []) as AssignmentWithDetails[]
  },

  /**
   * Get a single assignment by ID with full details
   */
  async getAssignmentById(
    assignmentId: string
  ): Promise<AssignmentWithDetails | null> {
    const { data, error } = await (supabase
      .from('assignments')
      .select(
        `
        *,
        client:clients (id, full_name, email),
        program:programs (id, title, description, duration_weeks)
      `
      )
      .eq('id' as any, assignmentId)
      .single() as any)

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to fetch assignment:', error)
      throw error
    }

    // Get progress summary
    const { data: progressData } = await (supabase.rpc(
      'get_assignment_progress_summary',
      { p_assignment_id: assignmentId }
    ) as any)

    const assignment = data as AssignmentWithDetails
    if (progressData && progressData.length > 0) {
      assignment.progress_summary = {
        total_items: progressData[0].total_items,
        completed_items: progressData[0].completed_items,
        progress_percent: progressData[0].progress_percent || 0,
      }
    }

    return assignment
  },

  /**
   * Create a new assignment
   */
  async createAssignment(
    assignment: AssignmentInsert
  ): Promise<Assignment> {
    const { data, error } = await (supabase
      .from('assignments')
      .insert(assignment as any)
      .select()
      .single() as any)

    if (error) {
      console.error('Failed to create assignment:', error)
      throw error
    }

    return data as Assignment
  },

  /**
   * Create multiple assignments at once (bulk assign)
   */
  async createBulkAssignments(
    assignments: AssignmentInsert[]
  ): Promise<{ created: number; failed: number; errors: string[] }> {
    const results = {
      created: 0,
      failed: 0,
      errors: [] as string[],
    }

    // Insert all assignments - Supabase handles bulk insert
    const { data, error } = await (supabase
      .from('assignments')
      .insert(assignments as any)
      .select() as any)

    if (error) {
      console.error('Failed to create bulk assignments:', error)
      // If bulk insert fails, try individual inserts to get partial success
      for (const assignment of assignments) {
        try {
          await this.createAssignment(assignment)
          results.created++
        } catch (err) {
          results.failed++
          results.errors.push(
            `Failed to assign to client ${assignment.client_id}: ${err instanceof Error ? err.message : 'Unknown error'}`
          )
        }
      }
    } else {
      results.created = data?.length || assignments.length
    }

    return results
  },

  /**
   * Update an existing assignment
   */
  async updateAssignment(
    assignmentId: string,
    updates: AssignmentUpdate
  ): Promise<Assignment | null> {
    const { data, error } = await (supabase
      .from('assignments')
      .update(updates as any)
      .eq('id' as any, assignmentId)
      .select()
      .single() as any)

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to update assignment:', error)
      throw error
    }

    return data as Assignment
  },

  /**
   * Cancel an assignment
   */
  async cancelAssignment(assignmentId: string): Promise<Assignment | null> {
    return this.updateAssignment(assignmentId, { status: 'cancelled' })
  },

  /**
   * Complete an assignment
   */
  async completeAssignment(assignmentId: string): Promise<Assignment | null> {
    return this.updateAssignment(assignmentId, { status: 'completed' })
  },

  /**
   * Delete an assignment
   */
  async deleteAssignment(assignmentId: string): Promise<boolean> {
    const { error } = await (supabase
      .from('assignments')
      .delete()
      .eq('id' as any, assignmentId) as any)

    if (error) {
      console.error('Failed to delete assignment:', error)
      throw error
    }

    return true
  },

  /**
   * Get assignments for a client (via token validation)
   */
  async getClientAssignments(
    clientId: string
  ): Promise<AssignmentWithDetails[]> {
    const { data, error } = await (supabase
      .from('assignments')
      .select(
        `
        *,
        client:clients (id, full_name, email),
        program:programs (id, title, description, duration_weeks)
      `
      )
      .eq('client_id' as any, clientId)
      .eq('status' as any, 'active')
      .order('assigned_at', { ascending: false }) as any)

    if (error) {
      console.error('Failed to fetch client assignments:', error)
      throw error
    }

    return (data || []) as AssignmentWithDetails[]
  },

  /**
   * Validate an assignment access token and get client info
   */
  async validateAssignmentToken(
    token: string
  ): Promise<{ assignment: Assignment; client: Client; isValid: boolean } | null> {
    const { data, error } = await (supabase
      .from('assignments')
      .select(`
        *,
        client:clients (*)
      `)
      .eq('access_token' as any, token)
      .single() as any)

    if (error || !data) {
      return null
    }

    const isValid =
      data.status === 'active' &&
      (!data.expires_at || new Date(data.expires_at) > new Date())

    return {
      assignment: data as Assignment,
      client: data.client as Client,
      isValid
    }
  },

  /**
   * Get assignment with full program details and progress (for client portal)
   */
  async getAssignmentWithProgress(
    assignmentId: string,
    clientId: string
  ): Promise<AssignmentWithProgress | null> {
    // Get assignment with program and client
    const { data: assignment, error: assignmentError } = await (supabase
      .from('assignments')
      .select(
        `
        *,
        client:clients (*),
        program:programs (*)
      `
      )
      .eq('id' as any, assignmentId)
      .single() as any)

    if (assignmentError) {
      if (assignmentError.code === 'PGRST116') return null
      throw assignmentError
    }

    // Get program items with videos
    const { data: items, error: itemsError } = await (supabase
      .from('program_items')
      .select(
        `
        *,
        video:videos (id, title, thumbnail_url, duration_seconds, mux_playback_id, status)
      `
      )
      .eq('program_id' as any, assignment.program.id)
      .order('position', { ascending: true }) as any)

    if (itemsError) throw itemsError

    // Get progress for all items
    const { data: progress, error: progressError } = await (supabase
      .from('assignment_progress')
      .select('*')
      .eq('assignment_id' as any, assignmentId)
      .eq('client_id' as any, clientId) as any)

    if (progressError) throw progressError

    // Map progress to items
    const progressMap = new Map(
      (progress || []).map((p: any) => [p.program_item_id, p])
    )

    const itemsWithProgress = (items || []).map((item: any) => ({
      ...item,
      video: item.video as Pick<
        Video,
        'id' | 'title' | 'thumbnail_url' | 'duration_seconds' | 'mux_playback_id' | 'status'
      >,
      progress: progressMap.get(item.id) || null,
    }))

    return {
      ...assignment,
      program: {
        ...assignment.program,
        items: itemsWithProgress,
      },
      client: assignment.client,
    } as AssignmentWithProgress
  },

  /**
   * Update progress for a video in an assignment
   */
  async updateProgress(
    assignmentId: string,
    programItemId: string,
    progressData: {
      video_id: string
      client_id: string
      watched_seconds: number
      completed?: boolean
    }
  ): Promise<AssignmentProgress> {
    // Use the database function to handle completion logic
    const { data, error } = await (supabase.rpc('mark_video_completed', {
      p_assignment_id: assignmentId,
      p_program_item_id: programItemId,
      p_video_id: progressData.video_id,
      p_client_id: progressData.client_id,
      p_watched_seconds: progressData.watched_seconds,
      p_force_complete: progressData.completed ?? false,
    }) as any)

    if (error) {
      console.error('Failed to update progress:', error)
      throw error
    }

    return data as AssignmentProgress
  },

  /**
   * Get progress for an assignment
   */
  async getAssignmentProgress(
    assignmentId: string,
    clientId: string
  ): Promise<AssignmentProgress[]> {
    const { data, error } = await (supabase
      .from('assignment_progress')
      .select('*')
      .eq('assignment_id' as any, assignmentId)
      .eq('client_id' as any, clientId) as any)

    if (error) {
      console.error('Failed to fetch progress:', error)
      throw error
    }

    return (data || []) as AssignmentProgress[]
  },

  /**
   * Get assignment access link
   */
  getAssignmentAccessLink(accessToken: string): string {
    return `${window.location.origin}/a/${accessToken}`
  },

  /**
   * Get active assignment count for a provider
   */
  async getActiveAssignmentCount(providerId: string): Promise<number> {
    const { count, error } = await (supabase
      .from('assignments')
      .select('*', { count: 'exact', head: true })
      .eq('provider_id' as any, providerId)
      .eq('status' as any, 'active') as any)

    if (error) {
      console.error('Failed to get assignment count:', error)
      return 0
    }

    return count || 0
  },

  /**
   * Get client IDs who already have an active/completed assignment for a specific program
   */
  async getClientsWithProgramAssignment(
    providerId: string,
    programId: string
  ): Promise<string[]> {
    const { data, error } = await (supabase
      .from('assignments')
      .select('client_id')
      .eq('provider_id' as any, providerId)
      .eq('program_id' as any, programId)
      .in('status' as any, ['active', 'completed']) as any)

    if (error) {
      console.error('Failed to fetch existing assignments:', error)
      throw error
    }

    return (data || []).map((a: any) => a.client_id)
  },

  /**
   * Get assignment summaries for multiple programs at once (for ProgramsPage efficiency)
   * Returns a Map keyed by program_id with aggregated stats
   */
  async getAssignmentSummariesForPrograms(
    providerId: string,
    programIds: string[]
  ): Promise<Map<string, AssignmentSummary>> {
    if (programIds.length === 0) {
      return new Map()
    }

    // Fetch all assignments for the specified programs
    // Note: progress_summary is computed, not a column - we'll fetch progress separately
    const { data: assignments, error } = await (supabase
      .from('assignments')
      .select('id, program_id, status, expires_at, updated_at')
      .eq('provider_id' as any, providerId)
      .in('program_id' as any, programIds) as any)

    if (error) {
      console.error('Failed to fetch assignment summaries:', error)
      throw error
    }

    // Initialize summaries for all requested programs
    const summaries = new Map<string, AssignmentSummary>()
    for (const programId of programIds) {
      summaries.set(programId, {
        program_id: programId,
        active_count: 0,
        completed_count: 0,
        expired_count: 0,
        total_count: 0,
        average_progress: 0,
        last_activity: undefined,
      })
    }

    if (!assignments || assignments.length === 0) {
      return summaries
    }

    // Fetch progress for all assignments in one query
    const assignmentIds = assignments.map((a: any) => a.id)
    const { data: progressData } = await (supabase.rpc(
      'get_assignments_progress_batch',
      { p_assignment_ids: assignmentIds }
    ) as any)

    // Create a map of assignment_id -> progress_percent
    const progressMap = new Map<string, number>()
    if (progressData) {
      for (const p of progressData) {
        progressMap.set(p.assignment_id, p.progress_percent || 0)
      }
    }

    // Aggregate data
    const progressTotals = new Map<string, { sum: number; count: number }>()

    for (const assignment of assignments) {
      const summary = summaries.get(assignment.program_id)
      if (!summary) continue

      summary.total_count++

      // Determine status (check expiration for active assignments)
      const isExpired =
        assignment.status === 'active' &&
        assignment.expires_at &&
        new Date(assignment.expires_at) < new Date()

      if (assignment.status === 'completed') {
        summary.completed_count++
      } else if (isExpired) {
        summary.expired_count++
      } else if (assignment.status === 'active') {
        summary.active_count++
      }

      // Track progress for averaging (from batch query)
      const progressPercent = progressMap.get(assignment.id) || 0
      if (!progressTotals.has(assignment.program_id)) {
        progressTotals.set(assignment.program_id, { sum: 0, count: 0 })
      }
      const totals = progressTotals.get(assignment.program_id)!
      totals.sum += progressPercent
      totals.count++

      // Track most recent activity
      if (
        assignment.updated_at &&
        (!summary.last_activity || assignment.updated_at > summary.last_activity)
      ) {
        summary.last_activity = assignment.updated_at
      }
    }

    // Calculate averages
    for (const [programId, totals] of progressTotals) {
      const summary = summaries.get(programId)
      if (summary && totals.count > 0) {
        summary.average_progress = Math.round(totals.sum / totals.count)
      }
    }

    return summaries
  },

  /**
   * Get all assignments for a specific program with full details
   * (for ProgramDetailPage assignments tab)
   */
  async getAssignmentsForProgram(
    providerId: string,
    programId: string
  ): Promise<AssignmentWithDetails[]> {
    const { data, error } = await (supabase
      .from('assignments')
      .select(
        `
        *,
        client:clients (id, full_name, email),
        program:programs (id, title, description, duration_weeks)
      `
      )
      .eq('provider_id' as any, providerId)
      .eq('program_id' as any, programId)
      .order('created_at', { ascending: false }) as any)

    if (error) {
      console.error('Failed to fetch program assignments:', error)
      throw error
    }

    // Enrich with progress summaries
    const assignments = (data || []) as AssignmentWithDetails[]

    // Fetch progress summaries for each assignment
    if (assignments.length > 0) {
      for (const assignment of assignments) {
        // Get progress summary for each assignment
        const { data: progressData } = await (supabase.rpc(
          'get_assignment_progress_summary',
          { p_assignment_id: assignment.id }
        ) as any)

        if (progressData && progressData.length > 0) {
          assignment.progress_summary = {
            total_items: progressData[0].total_items,
            completed_items: progressData[0].completed_items,
            progress_percent: progressData[0].progress_percent || 0,
          }
        }
      }
    }

    return assignments
  },

  /**
   * Calculate access expiration based on subscription tier
   */
  getAccessExpirationForTier(
    tier: 'free' | 'basic' | 'advanced' | 'corporate' | 'platinum'
  ): Date {
    const now = new Date()
    const daysMap: Record<string, number> = {
      free: 0, // Free tier can't assign
      basic: 30,
      advanced: 90,
      corporate: 90,
      platinum: 365,
    }

    const days = daysMap[tier] || 30
    now.setDate(now.getDate() + days)
    return now
  },

  /**
   * Calculate access expiration based on program duration in weeks.
   * Falls back to tier-based calculation if duration is not set.
   *
   * @param durationWeeks - Program duration in weeks (from programs.duration_weeks)
   * @param fallbackTier - Optional subscription tier for fallback if duration is null
   * @returns Expiration date
   */
  getAccessExpirationForProgramDuration(
    durationWeeks: number | null | undefined,
    fallbackTier?: 'free' | 'basic' | 'advanced' | 'corporate' | 'platinum'
  ): Date {
    const now = new Date()

    if (durationWeeks && durationWeeks > 0) {
      // Calculate from program duration (weeks * 7 days)
      now.setDate(now.getDate() + durationWeeks * 7)
      return now
    }

    // Fallback: If no duration set, use tier-based calculation or default to 30 days
    if (fallbackTier) {
      return this.getAccessExpirationForTier(fallbackTier)
    }

    // Ultimate fallback: 30 days
    now.setDate(now.getDate() + 30)
    return now
  },

  // ============================================
  // Email Notification Methods
  // ============================================

  /**
   * Check if an email can be resent (cooldown check)
   * Returns true if cooldown has passed or email was never sent
   */
  canResendEmail(emailSentAt: string | null): boolean {
    if (!emailSentAt) return true
    const lastSent = new Date(emailSentAt)
    const now = new Date()
    const diffMinutes = (now.getTime() - lastSent.getTime()) / (1000 * 60)
    return diffMinutes >= EMAIL_COOLDOWN_MINUTES
  },

  /**
   * Get minutes remaining until resend is allowed
   * Returns 0 if resend is allowed
   */
  getMinutesUntilResend(emailSentAt: string | null): number {
    if (!emailSentAt) return 0
    const lastSent = new Date(emailSentAt)
    const now = new Date()
    const diffMinutes = (now.getTime() - lastSent.getTime()) / (1000 * 60)
    return Math.max(0, Math.ceil(EMAIL_COOLDOWN_MINUTES - diffMinutes))
  },

  /**
   * Update email tracking status for an assignment
   */
  async updateEmailStatus(
    assignmentId: string,
    emailSentAt: string | null,
    errorMessage: string | null
  ): Promise<void> {
    // First get current send count
    const { data: current } = await (supabase
      .from('assignments')
      .select('email_send_count')
      .eq('id' as any, assignmentId)
      .single() as any)

    const updates: Record<string, unknown> = {
      email_last_error: errorMessage,
    }

    if (emailSentAt) {
      updates.email_sent_at = emailSentAt
      updates.email_send_count = (current?.email_send_count || 0) + 1
    }

    await (supabase
      .from('assignments')
      .update(updates as any)
      .eq('id' as any, assignmentId) as any)
  },

  /**
   * Send assignment notification email and update tracking
   * If exerciseCount is 0 or not provided, fetches the actual count from the database
   */
  async sendAssignmentEmail(
    assignment: AssignmentWithClientEmail,
    programTitle: string,
    coachName: string,
    exerciseCount: number = 0
  ): Promise<AssignmentEmailResult> {
    // Check cooldown
    if (!this.canResendEmail(assignment.email_sent_at)) {
      return {
        assignmentId: assignment.id,
        success: false,
        error: `Please wait ${this.getMinutesUntilResend(assignment.email_sent_at)} minutes before resending`,
      }
    }

    // Check if client has email
    if (!assignment.client?.email) {
      await this.updateEmailStatus(assignment.id, null, 'Client has no email address')
      return {
        assignmentId: assignment.id,
        success: false,
        error: 'Client has no email address',
      }
    }

    // Fetch actual exercise count if not provided
    let actualExerciseCount = exerciseCount
    if (actualExerciseCount === 0 && assignment.program_id) {
      const { count } = await supabase
        .from('program_items')
        .select('*', { count: 'exact', head: true })
        .eq('program_id', assignment.program_id)
      actualExerciseCount = count || 0
    }

    const accessLink = this.getAssignmentAccessLink(assignment.access_token!)

    const result = await emailService.sendAssignmentNotification(
      assignment.client.email,
      {
        coachName,
        playlistName: programTitle,
        exerciseCount: actualExerciseCount,
        assignmentId: assignment.id,
        accessLink,
      }
    )

    // Update assignment with email status
    if (result.success) {
      await this.updateEmailStatus(assignment.id, new Date().toISOString(), null)
    } else {
      await this.updateEmailStatus(assignment.id, null, result.error || 'Failed to send email')
    }

    return {
      assignmentId: assignment.id,
      success: result.success,
      error: result.error,
    }
  },

  /**
   * Send bulk assignment emails with rate limiting
   * Processes emails in batches of 5 to avoid overwhelming the API
   */
  async sendBulkAssignmentEmails(
    assignments: AssignmentWithClientEmail[],
    programTitle: string,
    coachName: string,
    exerciseCount: number
  ): Promise<AssignmentEmailResult[]> {
    const results: AssignmentEmailResult[] = []
    const BATCH_SIZE = 5

    for (let i = 0; i < assignments.length; i += BATCH_SIZE) {
      const batch = assignments.slice(i, i + BATCH_SIZE)
      const batchResults = await Promise.allSettled(
        batch.map((assignment) =>
          this.sendAssignmentEmail(assignment, programTitle, coachName, exerciseCount)
        )
      )

      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          results.push(result.value)
        } else {
          results.push({
            assignmentId: batch[index].id,
            success: false,
            error: result.reason?.message || 'Unknown error',
          })
        }
      })
    }

    return results
  },

  /**
   * Create multiple assignments and return full details with client info
   * (Used for immediate email sending after creation)
   */
  async createBulkAssignmentsWithDetails(
    assignments: AssignmentInsert[]
  ): Promise<{ assignments: AssignmentWithClientEmail[]; errors: string[] }> {
    const { data, error } = await (supabase
      .from('assignments')
      .insert(assignments as any)
      .select(`
        *,
        client:clients (id, full_name, email)
      `) as any)

    if (error) {
      console.error('Failed to create bulk assignments:', error)
      throw error
    }

    return {
      assignments: (data || []) as AssignmentWithClientEmail[],
      errors: [],
    }
  },
}
